import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, Card, CardContent, Spinner } from '@/components/ui/misc';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** Page frame shared by every admin screen: h1, one line of context, and header actions. */
export function AdminPage({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold">{title}</h1>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Right-aligns the cell; numbers are also rendered with tabular figures. */
  numeric?: boolean;
  cell: (row: T) => ReactNode;
}

/**
 * The one table every admin list uses: a real `<table>` with a caption and row headers, an
 * explicit empty state, and pending/error states that never leave the screen blank.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  isPending,
  error,
  empty,
  footer,
}: {
  caption: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isPending?: boolean;
  error?: ReactNode;
  empty: string;
  footer?: ReactNode;
}) {
  if (isPending) return <Spinner />;
  if (error) return <Alert variant="error">{error}</Alert>;

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={cn('px-3 py-2 font-medium', c.numeric && 'text-right')}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)} className="border-b last:border-b-0 align-middle">
                  {columns.map((c, i) =>
                    i === 0 ? (
                      <th key={c.key} scope="row" className="px-3 py-2 text-left font-normal">
                        {c.cell(row)}
                      </th>
                    ) : (
                      <td
                        key={c.key}
                        className={cn('px-3 py-2', c.numeric && 'text-right tabular-nums')}
                      >
                        {c.cell(row)}
                      </td>
                    ),
                  )}
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="p-6 text-center text-sm text-muted-foreground"
                  >
                    {empty}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {footer}
      </CardContent>
    </Card>
  );
}

/** Page x of y with previous/next, rendered only when there is more than one page. */
export function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;
  return (
    <nav
      aria-label={t('admin.pagination')}
      className="flex items-center justify-between border-t px-3 py-2 text-sm"
    >
      <p aria-live="polite" className="text-muted-foreground">
        {t('admin.pageOf', { page, totalPages, total })}
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          {t('app.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          {t('app.next')}
        </Button>
      </div>
    </nav>
  );
}

/**
 * Confirmation before something irreversible. `confirmWord` turns it into a typed confirmation
 * (anonymizing a user), which is the one case where a single click is not enough.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmWord,
  destructive = true,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  confirmWord?: string;
  destructive?: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const id = useId();
  const ready = !confirmWord || typed === confirmWord;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setTyped('');
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {confirmWord ? (
          <div className="space-y-1.5">
            <Label htmlFor={id}>{t('admin.typeToConfirm', { word: confirmWord })}</Label>
            <Input
              id={id}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('app.cancel')}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={!ready}
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface Option {
  id: string;
  label: string;
  hint?: string;
}

/**
 * Checkbox list in a popover for "which of these does this belong to". Real checkboxes, so the
 * whole thing is keyboard-operable and announces its state without any extra wiring.
 */
export function MultiSelect({
  options,
  selected,
  onChange,
  label,
  emptyLabel,
  disabled,
}: {
  options: Option[];
  selected: string[];
  onChange: (ids: string[]) => void;
  label: string;
  emptyLabel: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const chosen = new Set(selected);
  const summary =
    selected.length === 0
      ? emptyLabel
      : options
          .filter((o) => chosen.has(o.id))
          .map((o) => o.label)
          .join(', ');

  return (
    <Popover>
      <PopoverTrigger
        disabled={disabled}
        aria-label={label}
        className="inline-flex min-h-9 w-full items-center justify-between rounded-md border bg-background px-3 py-1.5 text-left text-sm disabled:opacity-50"
      >
        <span className={cn('truncate', selected.length === 0 && 'text-muted-foreground')}>
          {summary}
        </span>
        <span className="ml-2 shrink-0 text-xs text-muted-foreground">
          {selected.length > 0 ? selected.length : ''}
        </span>
      </PopoverTrigger>
      <PopoverContent className="max-h-72 w-72 overflow-y-auto p-2">
        <fieldset>
          <legend className="sr-only">{label}</legend>
          {options.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">{t('admin.nothingToChoose')}</p>
          ) : null}
          {options.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={chosen.has(option.id)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, option.id]
                      : selected.filter((id) => id !== option.id),
                  )
                }
              />
              <span>
                <span className="block">{option.label}</span>
                {option.hint ? (
                  <span className="block text-xs text-muted-foreground">{option.hint}</span>
                ) : null}
              </span>
            </label>
          ))}
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}
